import React, { useState, useEffect } from 'react';
import SidebarItem from './SidebarItem';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  Policy as PolicyIcon,
  Security as SecurityIcon,
  MoreHoriz as MoreHorizIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Logo from './Logo';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Modal
} from '@mui/material';
import { grey } from '@mui/material/colors';
import IntakeSummary from './IntakeSummary';
import MatchSummary from './MatchSummary';
import UserMenu from './UserMenu';
import ModeToggle from './ModeToggle';
import ActionIcons from './ActionIcons';
import EmptySearchStrategy from './EmptySearchStrategy';
import SettingsMenu from './SettingsMenu';

const Sidebar = ({
  side,
  isOpen,
  chatSessions,
  matchResults,
  activeSessionId,
  matchCount,
  userInfo,
  onToggle,
  onRefresh,
  onDelete,
  onNewChat, // Function to create a new chat session
  onClickSession,
  onClearAllSessions, // Function to clear all chat sessions
  anchor,
  rightSidebarTitle,
  queries, // Add queries parameter
  matchesLoading, // Add matchesLoading parameter
  icpRequirements, // Add centralized ICP requirements
  children
}) => {
  const isLeft = side === 'left';
  const drawerWidth = 250;
  const collapsedWidth = 50;
  const rightSidebarWidth = 456; // Updated to match Figma design (Tear Slip AI Block width)

  // State for mode toggle (plan/act)
  const [mode, setMode] = useState('plan');
  
  // State for user menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // State for chat session action menu
  const [sessionMenuAnchorEl, setSessionMenuAnchorEl] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const sessionMenuOpen = Boolean(sessionMenuAnchorEl);
  
  // State for settings modal
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleModeChange = (newMode) => {
    setMode(newMode);
  };
  
  // User menu functions
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Settings functions
  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };
  
  // Session menu functions
  const handleSessionMenuClick = (event, sessionId) => {
    event.stopPropagation(); // Prevent triggering the ListItem click
    setSessionMenuAnchorEl(event.currentTarget);
    setSelectedSessionId(sessionId);
  };

  const handleSessionMenuClose = () => {
    setSessionMenuAnchorEl(null);
    setSelectedSessionId(null);
  };
  
  const handleDeleteSession = () => {
    if (selectedSessionId && onDelete) {
      onDelete(selectedSessionId);
      handleSessionMenuClose();
    }
  };
  
  // Logout function
  const logout = () => {
    window.location.href = 'https://matches.findem.ai/auth/logout';
  };
  
  // Privacy Policy function
  const openPrivacyPolicy = () => {
    window.open('https://www.findem.ai/privacy-policy', '_blank');
    handleMenuClose();
  };
  
  // Report Security Issue function
  const openSecurityReport = () => {
    window.open('https://www.findem.ai/report-security-issue', '_blank');
    handleMenuClose();
  };

  const [showAllSessions, setShowAllSessions] = useState(false);
  const MAX_VISIBLE_SESSIONS = 5; // Maximum number of sessions to show initially

  const groupChatSessionsByTime = () => {
    if (!Array.isArray(chatSessions)) {
      console.error('chatSessions is not an array:', chatSessions);
      return [];
    }

    const now = Date.now();
    const groupedSessions = {
      'A few minutes ago': [],
      'A few hours ago': [],
      Yesterday: [],
      'Last week': [],
      'Earlier this month': [],
      Older: []
    };

    chatSessions.forEach((session) => {
      const diff = now - session.timestamp;
      if (diff < 1000 * 60 * 60) {
        groupedSessions['A few minutes ago'].push(session);
      } else if (diff < 1000 * 60 * 60 * 24) {
        groupedSessions['A few hours ago'].push(session);
      } else if (diff < 1000 * 60 * 60 * 24 * 2) {
        groupedSessions['Yesterday'].push(session);
      } else if (diff < 1000 * 60 * 60 * 24 * 7) {
        groupedSessions['Last week'].push(session);
      } else if (diff < 1000 * 60 * 60 * 24 * 30) {
        groupedSessions['Earlier this month'].push(session);
      } else {
        groupedSessions['Older'].push(session);
      }
    });

    return Object.entries(groupedSessions).filter(
      ([, sessions]) => sessions.length > 0
    );
  };

  const allGroupedChatSessions = isLeft
    ? chatSessions
      ? groupChatSessionsByTime()
      : []
    : [];
    
  // Calculate total number of sessions
  const totalSessions = allGroupedChatSessions.reduce(
    (total, [_, sessions]) => total + sessions.length, 
    0
  );
  
  // Determine if we need to show the "More" button
  const hasMoreSessions = totalSessions > MAX_VISIBLE_SESSIONS;
  
  // Limit the number of sessions shown if not showing all
  const groupedChatSessions = isLeft
    ? !showAllSessions && hasMoreSessions
      ? limitSessionsToShow(allGroupedChatSessions, MAX_VISIBLE_SESSIONS)
      : allGroupedChatSessions
    : [];
    
  // Function to limit the number of sessions shown
  function limitSessionsToShow(groupedSessions, maxSessions) {
    let count = 0;
    const limitedGroups = [];
    
    for (const group of groupedSessions) {
      const [groupName, sessions] = group;
      
      if (count + sessions.length <= maxSessions) {
        // Add the entire group
        limitedGroups.push(group);
        count += sessions.length;
      } else {
        // Add a partial group
        const remainingSessions = maxSessions - count;
        if (remainingSessions > 0) {
          limitedGroups.push([groupName, sessions.slice(0, remainingSessions)]);
        }
        break;
      }
    }
    
    return limitedGroups;
  }
  
  // Handle click on "More" button
  const handleShowMoreSessions = () => {
    setShowAllSessions(true);
  };

  const handleRefresh = React.useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  function openDiffSession(sessionId) {
    onClickSession(sessionId);
  }

  return (
    <>
    <Drawer
      variant="permanent"
      open={isOpen}
      anchor={anchor}
      PaperProps={{
          sx: {
            width: isOpen
              ? isLeft
                ? drawerWidth
                : rightSidebarWidth
              : collapsedWidth,
            bgcolor: '#111827',
            borderRight: isLeft ? '1px solid #1E293B' : '1px solid #1E293B',
            borderLeft: !isLeft ? '1px solid #1E293B' : 0,
            transition: 'width 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            overflowY: 'auto',
            position: 'fixed',
            height: '100vh',
            zIndex: 1200,
            top: 0,
            bottom: 0,
            maxWidth: isOpen
              ? isLeft
                ? drawerWidth
                : rightSidebarWidth
              : collapsedWidth
          }
      }}
    >
      {isLeft && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              bgcolor: '#111827',
              height: '80px',
              width: '100%',
              // borderBottom: '1px solid #222B3A',
              padding: '0 20px'
            }}
          >
            <Logo
              variant="full"
              size="small"
              isOpen={isOpen}
              isLeft={isLeft}
              onToggle={onToggle}
            />
          </Box>
        </>
      )}
      {isLeft && (
        <Box sx={{ pt: 0.5, pb: 0, px: isOpen ? 2.25 : 1.125 }}>
          <List sx={{ p: 0 }}>
            {/* New Intake */}
            <ListItem 
              button 
              onClick={onNewChat}
              sx={{ 
                py: 1, 
                px: 0.875,
                gap: '6px',
                justifyContent: isOpen ? 'flex-start' : 'center',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                borderRadius: '8px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 0.75 : 0 }}>
                <AddIcon sx={{ color: 'white', fontSize: 18 }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText 
                  primary="New intake" 
                  primaryTypographyProps={{ 
                    sx: { 
                      color: 'white', 
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '1.43em'
                    } 
                  }} 
                />
              )}
            </ListItem>
            
            {/* Search */}
            <ListItem 
              button 
              sx={{ 
                py: 1, 
                px: 0.875,
                gap: '6px',
                justifyContent: isOpen ? 'flex-start' : 'center',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                borderRadius: '8px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 0.75 : 0 }}>
                <SearchIcon sx={{ color: 'white', fontSize: 18 }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText 
                  primary="Search" 
                  primaryTypographyProps={{ 
                    sx: { 
                      color: 'white', 
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '1.43em'
                    } 
                  }} 
                />
              )}
            </ListItem>
            
            {/* Chats */}
            <ListItem 
              button 
              sx={{ 
                py: 1, 
                px: 0.875,
                gap: '6px',
                justifyContent: isOpen ? 'flex-start' : 'center',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                borderRadius: '8px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 0.75 : 0 }}>
                <ChatIcon sx={{ color: 'white', fontSize: 18 }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText 
                  primary="Chats" 
                  primaryTypographyProps={{ 
                    sx: { 
                      color: 'white', 
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '1.43em'
                    } 
                  }} 
                />
              )}
            </ListItem>
          </List>
        </Box>
      )}
      <Box sx={{ py: 1, mt:4, flex: 1, overflow: 'auto', backgroundColor: '#111827' }}>
        {isLeft && isOpen && (
          <>
            {/* Chat sessions grouped by time */}
            {groupedChatSessions.length > 0 ? (
              <>
                {groupedChatSessions.map(([groupName, sessions]) => (
                  <React.Fragment key={groupName}>
                    {/* Group header */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        px: 2, 
                        py: 1, 
                        color: 'rgba(255, 255, 255, 0.8)', 
                        fontFamily: 'Roboto',
                        fontSize: 14,
                        fontWeight: 700,
                        lineHeight: '1.43em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      {groupName}
                    </Typography>
                    
                    {/* Sessions in this group */}
                    <List sx={{ pt: 0, pb: 1 }}>
                      {sessions.map(session => (
                        <ListItem
                          key={session.id}
                          button
                          selected={activeSessionId === session.id}
                          onClick={() => openDiffSession(session.id)}
                          sx={{
                            py: 1,
                            px: 0.875,
                            backgroundColor: activeSessionId === session.id ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                            borderRadius: '8px',
                            mx: 1,
                            mb: 0.5,
                            position: 'relative',
                            '& .action-icon': {
                              display: 'none'
                            },
                            '&:hover .action-icon': {
                              display: 'block'
                            },
                            width: 'calc(100% - 16px)', // Ensure the ListItem doesn't exceed container width
                            boxSizing: 'border-box',
                            overflowX: 'hidden' // Prevent horizontal overflow
                          }}
                        >
                          <ListItemText
                            primary={session.name || "Untitled Intake Meeting"}
                            primaryTypographyProps={{
                              sx: {
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontFamily: 'Roboto',
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: '1.43em',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                maxWidth: isOpen ? (drawerWidth - 80) : 0, // Limit text width to prevent overflow
                                boxSizing: 'border-box'
                              }
                            }}
                          />
                          <IconButton
                            className="action-icon"
                            size="small"
                            onClick={(e) => handleSessionMenuClick(e, session.id)}
                            sx={{
                              position: 'absolute',
                              right: 8,
                              color: 'rgba(255, 255, 255, 0.6)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'rgba(255, 255, 255, 0.9)'
                              }
                            }}
                          >
                            <MoreHorizIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  </React.Fragment>
                ))}
                
                {/* "More" button - only show if there are more sessions to display */}
                {!showAllSessions && hasMoreSessions && (
                  <ListItem 
                    button 
                    onClick={handleShowMoreSessions}
                    sx={{ 
                      py: 1, 
                      px: 0.875,
                      gap: '6px',
                      justifyContent: 'flex-start',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                      borderRadius: '8px',
                      mx: 1,
                      mb: 0.5,
                      width: 'calc(100% - 16px)', // Ensure the ListItem doesn't exceed container width
                      boxSizing: 'border-box',
                      overflowX: 'hidden' // Prevent horizontal overflow
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: 0.75 }}>
                      <ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="More" 
                      primaryTypographyProps={{ 
                        sx: { 
                          color: 'rgba(255, 255, 255, 0.8)', 
                          fontFamily: 'Roboto',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: '1.43em'
                        } 
                      }} 
                    />
                  </ListItem>
                )}
                
                {/* Clear All link - only show if there are chat sessions */}
                {groupedChatSessions.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      mt: 2,
                      ml:2,
                      mb: 1
                    }}
                  >
                    <Typography
                      onClick={onClearAllSessions}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.8)'
                        }
                      }}
                    >
                      Clear chat history
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <ListItem sx={{ px: 2, py: 1 }}>
                <ListItemText
                  primary="No chat sessions"
                  primaryTypographyProps={{
                    sx: { 
                      color: 'rgba(255, 255, 255, 0.5)', 
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      fontWeight: 400,
                      fontStyle: 'italic' 
                    }
                  }}
                />
              </ListItem>
            )}
          </>
        )}
        {!isLeft && (
          <Box
            sx={{
              backgroundColor: '#111827',
              display: isOpen ? 'flex' : 'none',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #1E293B'
              }}
            >
              {(
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{ color: '#E5E7EB' }}
                >
                  {rightSidebarTitle}
                </Typography>
              )}
              <IconButton
                onClick={handleRefresh}
                sx={{ 
                  p: 0.5, 
                  color: '#E5E7EB',
                  backgroundColor: '#374151',
                  '&:hover': { 
                    backgroundColor: '#4B5563'
                  }
                }}
                size="small"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
            {isOpen && !isLeft && (
              <ModeToggle
                mode={mode}
                onModeChange={handleModeChange}
                disabled={!matchResults}
              />
            )}
            <>
              {mode === 'plan' ? (
                <IntakeSummary
                  matchResults={matchResults}
                  matchCount={matchCount}
                  handleRefreshResults={onRefresh}
                  queries={queries}
                  onSaveSearch={onRefresh}
                  onOpenFindemSearch={onRefresh}
                  icpRequirements={icpRequirements}
                />
              ) : (
                matchResults ? (
                  <ActionIcons profiles={matchResults?.profiles} />
                ) : (
                  <EmptySearchStrategy loading={matchesLoading} />
                )
              )}
              {/* <MatchSummary matchCount={matchCount} /> */}
            </>
          </Box>
        )}
      </Box>
      {/* Settings and Help section */}
      {isLeft && (
        <Box sx={{ mt: 'auto', px: isOpen ? 2.25 : 1.125 }}>
          <List sx={{ p: 0 }}>
            <ListItem 
              button 
              onClick={handleSettingsOpen}
              sx={{ 
                py: 1, 
                px: 0.875,
                gap: '6px',
                justifyContent: isOpen ? 'flex-start' : 'center',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                borderRadius: '8px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 0.75 : 0 }}>
                <SettingsIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 18 }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText 
                  primary="Settings" 
                  primaryTypographyProps={{ 
                    sx: { 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '1.43em'
                    } 
                  }} 
                />
              )}
            </ListItem>
            
            <ListItem 
              button 
              sx={{ 
                py: 1, 
                px: 0.875,
                gap: '6px',
                justifyContent: isOpen ? 'flex-start' : 'center',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                borderRadius: '8px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 0.75 : 0 }}>
                <HelpIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 18 }} />
              </ListItemIcon>
              {isOpen && (
                <ListItemText 
                  primary="Help" 
                  primaryTypographyProps={{ 
                    sx: { 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      fontFamily: 'Roboto',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '1.43em'
                    } 
                  }} 
                />
              )}
            </ListItem>
          </List>
        </Box>
      )}
      
      {/* User profile section */}
      <Box
        sx={{
          width: '100%',
          borderTop: '1px solid #1E293B',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          justifyContent: 'flex-end',
          bottom: 0,
          position: 'sticky',
          backgroundColor: '#111827'
        }}
      >
        {isLeft && (
          isOpen ? (
            <Box 
              onClick={handleMenuClick}
              sx={{ 
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                m: 2,
                height: 58,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
              }}
            >
              <Avatar 
                src={userInfo?.photoURL} 
                alt={userInfo?.name || "User"}
                sx={{ width: 36, height: 36 }}
              />
              <Box sx={{ overflow: 'hidden' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'white', 
                    fontFamily: 'Roboto',
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: '1.17em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {userInfo?.name || "Luca Williamson"}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: 'Roboto',
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: '1.2em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {userInfo?.email || "@notion.io"}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box 
              onClick={handleMenuClick}
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <Avatar 
                src={userInfo?.photoURL} 
                alt={userInfo?.name || "User"}
                sx={{ width: 36, height: 36 }}
              />
            </Box>
          )
        )}
        
        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          PaperProps={{
            sx: {
              bgcolor: '#1F2937',
              color: '#E5E7EB',
              width: 220,
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
              border: '1px solid #374151'
            }
          }}
        >
          <MenuItem 
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Findem University
            </Typography>
          </MenuItem>
          
          <MenuItem 
            component="a"
            href="/settings"
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Settings
            </Typography>
          </MenuItem>
          
          <MenuItem 
            component="a"
            href="https://account.findem.ai"
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Account Management
            </Typography>
          </MenuItem>
          
          <MenuItem 
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Get Help
            </Typography>
          </MenuItem>
          
          <MenuItem 
            component="a"
            href="https://www.findem.ai/privacy-policy"
            target="_blank"
            onClick={handleMenuClose}
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Privacy Policy
            </Typography>
          </MenuItem>
          
          <MenuItem 
            component="a"
            href="https://www.findem.ai/report-security-issue"
            target="_blank"
            onClick={handleMenuClose}
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Report Security Issue
            </Typography>
          </MenuItem>
          
          <Divider sx={{ bgcolor: '#374151', my: 1 }} />
          
          <MenuItem 
            component="a"
            href="https://matches.findem.ai/auth/logout"
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Log Out
            </Typography>
          </MenuItem>
        </Menu>
        
        {/* Session Action Menu */}
        <Menu
          anchorEl={sessionMenuAnchorEl}
          open={sessionMenuOpen}
          onClose={handleSessionMenuClose}
          disablePortal
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          slotProps={{
            paper: {
              sx: {
                marginTop: '0px',
                marginLeft: '10px'
              }
            }
          }}
          PaperProps={{
            sx: {
              bgcolor: '#1F2937',
              color: '#E5E7EB',
              width: 180,
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
              border: '1px solid #374151'
            }
          }}
        >
          <MenuItem 
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <ShareIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 20 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Share
            </Typography>
          </MenuItem>
          
          <MenuItem 
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <EditIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 20 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Rename
            </Typography>
          </MenuItem>
          
          <MenuItem 
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <ArchiveIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 20 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Archive
            </Typography>
          </MenuItem>
          
          <Divider sx={{ bgcolor: '#374151', my: 1 }} />
          
          <MenuItem 
            onClick={handleDeleteSession}
            sx={{ 
              py: 1.5,
              '&:hover': { backgroundColor: '#374151' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <DeleteIcon sx={{ color: '#EF4444', fontSize: 20 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#EF4444',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Delete
            </Typography>
          </MenuItem>
        </Menu>
        
        {/* Toggle button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 1,
            color: '#E5E7EB',
            backgroundColor: '#111827',
            '&:hover': { 
              backgroundColor: '#1F2937'
            },
            cursor: 'pointer',
            display: 'none' // Hide the toggle button as it's now in the Logo component
          }}
          onClick={onToggle}
        >
          {isLeft ? (
            isOpen ? (
              <></>
            ) : (
              <></>
            )
          ) : isOpen ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </Box>
      </Box>
    </Drawer>

    {/* Settings Modal */}
    <Modal
      open={settingsOpen}
      onClose={handleSettingsClose}
      aria-labelledby="settings-modal-title"
      aria-describedby="settings-modal-description"
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2 
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          maxHeight: '95vh',
          maxWidth: '95vw',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <SettingsMenu 
          onClose={handleSettingsClose} 
          userId={userInfo?.uid || 'default_user'} 
        />
      </Box>
    </Modal>
    </>
  );
};

export default Sidebar;
